import {
  isDocumentTemplateIncludedInSoapNote,
  isDocumentTemplateType,
} from "./template-include-in-soap.util";

/** Encounter context titles used for each care-plan / frontend template id. */
const TEMPLATE_ID_TO_CONTEXT_TITLES: Record<string, string[]> = {
  "chief-complaint": ["Reason for Visit"],
  "hpi-frontend-template": ["History of Present Illness", "HPI"],
  "ros-frontend-template": ["Review of Systems", "ROS"],
  "history-frontend-template": ["Patient History", "History"],
  "med-req-frontend-template": [
    "Medication Reconciliation",
    "Current Medications",
    "Medications",
  ],
  "allergies-frontend-template": ["Allergies"],
  "medication-order-frontend-template": ["Medication Order"],
  "vitals-frontend-template": ["Vitals"],
  "lab-results-frontend-template": ["Lab Results"],
  "ultrasound-results-frontend-template": ["Ultrasound Results"],
  "last-encounter-frontend-template": ["Last Encounter"],
};

/** Title variants that refer to the same Last Indicated context row. */
const CONTEXT_TITLE_ALIAS_GROUPS: string[][] = [
  ["history of present illness", "hpi"],
  ["review of systems", "ros"],
  ["patient history", "history"],
  ["functional limitation", "functional limitations"],
  ["allergy", "allergies"],
  ["current medications", "medications"],
  ["additional info", "additional information"],
  ["indication", "indications"],
  ["operation title", "operation titles"],
  ["impression", "impressions"],
  ["sedation", "conscious sedation", "anesthesia", "moderate sedation"],
  ["recommendation", "recommendations", "recomendation", "recomendations"],
  ["description of procedure"],
  ["informed consent"],
  [
    "angiographic findings",
    "angiography",
    "angiogram",
    "angiographic results",
    "imaging findings",
    "imaging performed",
  ],
  ["last encounter"],
];

function normalizeContextLabel(value: string | null | undefined): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function expandContextTitleAliases(
  title: string | null | undefined
): Set<string> {
  const normalized = normalizeContextLabel(title);
  const aliases = new Set<string>();
  if (!normalized) {
    return aliases;
  }

  aliases.add(normalized);
  for (const group of CONTEXT_TITLE_ALIAS_GROUPS) {
    if (group.includes(normalized)) {
      group.forEach((entry) => aliases.add(entry));
    }
  }
  return aliases;
}

export function getEncounterContextTitlesForTemplateId(
  templateId: string | null | undefined
): string[] {
  const normalizedId = String(templateId ?? "").trim();
  if (!normalizedId) {
    return [];
  }
  return TEMPLATE_ID_TO_CONTEXT_TITLES[normalizedId] ?? [];
}

export function resolveContextMatchTitles(
  templateId: string | null | undefined,
  templateTitle?: string | null
): Set<string> {
  const titles = new Set<string>();

  getEncounterContextTitlesForTemplateId(templateId).forEach((title) => {
    expandContextTitleAliases(title).forEach((alias) => titles.add(alias));
  });

  expandContextTitleAliases(templateTitle).forEach((alias) => titles.add(alias));

  return titles;
}

export function encounterContextTitleMatchesTemplate(
  contextTitle: string | null | undefined,
  templateId: string | null | undefined,
  templateTitle?: string | null
): boolean {
  const normalizedContextTitle = normalizeContextLabel(contextTitle);
  if (!normalizedContextTitle) {
    return false;
  }

  const matchTitles = resolveContextMatchTitles(templateId, templateTitle);
  return matchTitles.has(normalizedContextTitle);
}

function hasMeaningfulText(value: unknown): boolean {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return false;
  }
  const plain = raw
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return plain.length > 0;
}

export function encounterContextHasUsableContent(
  ctx: unknown
): boolean {
  return !!resolveContextSummaryForNote(ctx);
}

/** Text to embed in SOAP/manual notes from an encounter context row. */
export function resolveContextSummaryForNote(ctx: unknown): string {
  if (!ctx || typeof ctx !== "object") {
    return "";
  }
  const row = ctx as Record<string, unknown>;
  const summary = String(row["summary"] ?? "").trim();
  if (hasMeaningfulText(summary)) {
    return summary;
  }
  const type = String(row["type"] ?? "")
    .trim()
    .toLowerCase();
  if (type === "html") {
    return "";
  }
  const template = String(row["template"] ?? "").trim();
  if (hasMeaningfulText(template)) {
    return template;
  }
  return "";
}

/**
 * Document templates marked not for SOAP, and consent/authorization forms that should
 * stay in the chart only (aligned with encounter-contexts listing exclusions).
 */
/** Encounter contexts eligible for SOAP note / plan-summary AI input. */
export function filterEncounterContextsForSoapNote<T>(contexts: T[] | null | undefined): T[] {
  if (!contexts?.length) {
    return [];
  }
  return contexts.filter((ctx) => !shouldExcludeContextFromSoapNote(ctx));
}

export function shouldExcludeContextFromSoapNote(ctx: unknown): boolean {
  if (!ctx || typeof ctx !== "object") {
    return false;
  }
  const row = ctx as Record<string, unknown>;
  const templateEntity = row["templateEntity"] as
    | Record<string, unknown>
    | undefined;
  if (
    templateEntity &&
    isDocumentTemplateType(templateEntity["templateType"])
  ) {
    if (!isDocumentTemplateIncludedInSoapNote(templateEntity)) {
      return true;
    }
  }
  const title = normalizeContextLabel(String(row["title"] ?? ""));
  if (!title) {
    return false;
  }
  if (title === "informed consent") {
    return false;
  }
  if (title === "consent") {
    return true;
  }
  const consentDocNeedles = [
    "authorization to release",
    "release medical information",
    "photo, video",
    "media consent",
    "consent & authorization",
    "consent and authorization",
    "patient request for specialty",
    "hipaa authorization",
    "privacy authorization",
  ];
  return consentDocNeedles.some((needle) => title.includes(needle));
}

/**
 * Match a saved encounter context to a care-plan template id.
 * Frontend templates often have no UUID templateId on the context row — match by title too.
 */
/** Encounter context row for a care-plan checklist item (template id + display title). */
export function findEncounterContextForCarePlanDetail(
  contexts: unknown[] | null | undefined,
  detail: { templateId?: string | null; title?: string | null }
): Record<string, unknown> | undefined {
  const templateId = String(detail?.templateId ?? "").trim();
  if (!templateId) {
    return undefined;
  }
  const templateTitle = String(detail?.title ?? "").trim();
  return (contexts ?? []).find((ctx) =>
    encounterContextMatchesTemplateId(
      ctx as Record<string, unknown>,
      templateId,
      templateTitle
    )
  ) as Record<string, unknown> | undefined;
}

export function encounterContextMatchesTemplateId(
  ctx: Record<string, unknown> | null | undefined,
  templateId: string | null | undefined,
  templateTitle?: string | null
): boolean {
  const normalizedTemplateId = String(templateId ?? "").trim();
  if (!normalizedTemplateId || !ctx) {
    return false;
  }

  if (String(ctx["templateId"] ?? "").trim() === normalizedTemplateId) {
    return true;
  }

  const carePlanDetail = ctx["carePlanDetail"] as
    | Record<string, unknown>
    | undefined;
  if (
    String(carePlanDetail?.["templateId"] ?? "").trim() === normalizedTemplateId
  ) {
    return true;
  }

  const templateEntity = ctx["templateEntity"] as
    | Record<string, unknown>
    | undefined;
  if (String(templateEntity?.["id"] ?? "").trim() === normalizedTemplateId) {
    return true;
  }

  if (String(templateEntity?.["id"] ?? "").trim() === normalizedTemplateId) {
    return true;
  }

  const contextLabels = [
    ctx["title"],
    ctx["displayName"],
    templateEntity?.["title"],
    templateEntity?.["displayName"],
  ];
  for (const label of contextLabels) {
    if (
      encounterContextTitleMatchesTemplate(
        String(label ?? ""),
        normalizedTemplateId,
        templateTitle
      )
    ) {
      return true;
    }
  }

  return false;
}

export function resolveContextTitleCandidatesForVersionHistory(
  templateId: string | null | undefined,
  templateTitle?: string | null
): string[] {
  const titles: string[] = [];
  const add = (value: string | null | undefined) => {
    const text = String(value ?? "").trim();
    if (text && !titles.includes(text)) {
      titles.push(text);
    }
  };

  getEncounterContextTitlesForTemplateId(templateId).forEach(add);
  add(templateTitle);

  return titles;
}

/** True when two context titles refer to the same document section (e.g. Indication / Indications). */
export function contextTitleMatchesAlias(
  contextTitle: string | null | undefined,
  referenceTitle: string | null | undefined
): boolean {
  const actual = normalizeContextLabel(contextTitle);
  if (!actual) {
    return false;
  }
  return expandContextTitleAliases(referenceTitle).has(actual);
}

export const LAST_INDICATED_MEDICATIONS_TEMPLATE_ID =
  "med-req-frontend-template";

export const LAST_ENCOUNTER_TEMPLATE_ID = "last-encounter-frontend-template";
